import { useMemo } from 'react';
import { useAppSelector } from './redux';
import { User, Membership, isAdminRole, isStaffOrAbove, isOrgLevelRole } from '../types';

export interface AuthContext {
  user: User | null;
  isAuthenticated: boolean;

  /** The active membership (selected via role switcher) */
  activeMembership: Membership | undefined;
  /** Index of the active membership in the memberships array */
  activeMembershipIndex: number;
  /** Active role as a string */
  activeRole: string;
  /** Active location ID (undefined for org-level roles) */
  activeLocationId: string | undefined;
  /** Active location name */
  activeLocationName: string | undefined;
  /** Active organization ID */
  activeOrganizationId: string | undefined;
  /** Active organization name */
  activeOrganizationName: string | undefined;

  /** Role checks */
  isParent: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  isLocationAdmin: boolean;
  isOrgAdmin: boolean;
  isSuperAdmin: boolean;
  /** ORG_ADMIN or SUPER_ADMIN */
  isOrgLevel: boolean;
  /** STAFF or above (includes all admin levels) */
  isStaffOrAbove: boolean;
}

/**
 * Central auth hook - extracts user, active membership, and role checks.
 * The active membership is determined by activeMembershipIndex in Redux state,
 * which is changeable via the role switcher dropdown in the Layout header.
 */
export function useAuth(): AuthContext {
  const { user, isAuthenticated, activeMembershipIndex } = useAppSelector((state) => state.auth);

  return useMemo(() => {
    const memberships = user?.memberships || [];
    const safeIndex = Math.min(activeMembershipIndex, Math.max(memberships.length - 1, 0));
    const activeMembership = memberships[safeIndex];
    const activeRole = activeMembership?.role ? String(activeMembership.role) : '';

    return {
      user,
      isAuthenticated,
      activeMembership,
      activeMembershipIndex: safeIndex,
      activeRole,
      activeLocationId: activeMembership?.locationId,
      activeLocationName: activeMembership?.locationName,
      activeOrganizationId: activeMembership?.organizationId,
      activeOrganizationName: activeMembership?.organizationName,

      isParent: activeRole === 'PARENT',
      isStaff: activeRole === 'STAFF',
      isAdmin: isAdminRole(activeRole),
      isLocationAdmin: activeRole === 'LOCATION_ADMIN',
      isOrgAdmin: activeRole === 'ORG_ADMIN',
      isSuperAdmin: activeRole === 'SUPER_ADMIN',
      isOrgLevel: isOrgLevelRole(activeRole),
      isStaffOrAbove: isStaffOrAbove(activeRole),
    };
  }, [user, isAuthenticated, activeMembershipIndex]);
}
